import React, { useState, useEffect, useRef } from 'react';
import './MultiSelectSupervisor.css';

function MultiSelectSupervisor({
  supervisors, // 모든 감리원 목록
  selectedSupervisors, // 현재 선택된 감리원 (배열)
  onSelectionChange, // 선택 변경 시 호출될 콜백 함수
  placeholder = "감리원을 선택하세요",
  disabled = false,
  duplicateCheckEntries = [], // 중복 체크를 위한 ScheduleEntry 배열
  isManager = true, // 책임감리원인지 보조감리원인지 구분
  otherSelectedSupervisors = [], // 다른 필드에서 선택된 감리원 (예: 책임감리원 필드에서 보조감리원 중복 체크용)
  specialNotes = [], // 특이사항 목록 추가
}) {
  console.log('MultiSelectSupervisor - selectedSupervisors:', selectedSupervisors);
  console.log('MultiSelectSupervisor - typeof selectedSupervisors:', typeof selectedSupervisors);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setSearchTerm(''); // 드롭다운 열릴 때 검색어 초기화
    }
  };

  const handleSelectSupervisor = (supervisorName) => {
    let newSelection;
    if (isManager) { // 책임감리원
      newSelection = selectedSupervisors.includes(supervisorName)
        ? selectedSupervisors.filter((name) => name !== supervisorName)
        : [...selectedSupervisors, supervisorName];
    } else { // 보조감리원
      if (supervisorName === '없음') {
        newSelection = selectedSupervisors.includes('없음') ? [] : ['없음']; // '없음' 선택/해제
      } else {
        // 다른 감리원 선택 시 '없음' 해제
        newSelection = selectedSupervisors.includes(supervisorName)
          ? selectedSupervisors.filter((name) => name !== supervisorName)
          : [...selectedSupervisors.filter(name => name !== '없음'), supervisorName];
      }
    }
    onSelectionChange(newSelection);
  };

  const getSupervisorStatus = (supervisorName) => {
    let status = '';
    let isRed = false;

    // 특이사항 체크 (specialNotes prop을 통해 전달됨)
    const hasSpecialNote = specialNotes.find(note => {
      const supervisor = supervisors.find(s => s.id === note.supervisorId);
      return supervisor && supervisor.name === supervisorName;
    });

    if (hasSpecialNote) {
      status = ` (${hasSpecialNote.noteType})`;
      isRed = true;
    }

    // 이미 배정된 감리원 체크 (duplicateCheckEntries는 ScheduleEntry 배열)
    // 해당 날짜에 이미 배정된 모든 감리원 목록을 Set으로 만듭니다.
    const allAssignedSupervisorsOnDate = new Set();
    duplicateCheckEntries.forEach(entry => {
      if (typeof entry.assignedManagers === 'string' && entry.assignedManagers) {
        entry.assignedManagers.split(',')
          .map(s => s.trim())
          .forEach(name => allAssignedSupervisorsOnDate.add(name));
      }
      if (typeof entry.assignedAssistants === 'string' && entry.assignedAssistants) {
        entry.assignedAssistants.split(',')
          .map(s => s.trim())
          .filter(assistant => assistant !== '없음')
          .forEach(name => allAssignedSupervisorsOnDate.add(name));
      }
    });

    // 현재 선택된 감리원 목록(selectedSupervisors)에 supervisorName이 포함되어 있지 않아야 중복으로 간주
    const isCurrentlySelected = selectedSupervisors.includes(supervisorName);

    // 새로 선택하려는 감리원이 이미 해당 날짜에 다른 공사에 배정되어 있는지 확인
    const isAlreadyAssignedOnDate = allAssignedSupervisorsOnDate.has(supervisorName);

    // 현재 선택된 감리원이 아니고, 이미 해당 날짜에 배정되어 있다면 중복으로 간주
    if (!isCurrentlySelected && isAlreadyAssignedOnDate && !isRed) {
      status = ' (중복 배정)';
      isRed = true;
    }

    // 다른 필드에서 선택된 감리원 체크 (예: 책임감리원 필드에서 보조감리원 중복 체크용)
    const isOtherSelected = otherSelectedSupervisors.includes(supervisorName);
    if (isOtherSelected && !isRed) {
      status = isManager ? ' (보조감리 선택됨)' : ' (책임감리 선택됨)';
      isRed = true;
    }

    return { status, isRed };
  };

  const filteredSupervisors = supervisors.filter((s) =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="multi-select-supervisor" ref={dropdownRef}>
      <div
        className={`selected-supervisors-display ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleToggleDropdown}
      >
        {Array.isArray(selectedSupervisors) && selectedSupervisors.length > 0 ? (
          selectedSupervisors.map((name) => (
            <span key={name} className="selected-tag">
              {name}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectSupervisor(name);
                  }}
                >
                  ×
                </button>
              )}
            </span>
          ))
        ) : (
          <span className="placeholder">{placeholder}</span>
        )}
      </div>
      {isOpen && !disabled && (
        <div className="dropdown-list">
          <input
            type="text"
            placeholder="검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()} // 인풋 클릭 시 드롭다운 닫히지 않도록
            className="search-input"
          />
          <ul>
            {!isManager && (
              <li>
                <label>
                  <input
                    type="checkbox"
                    checked={selectedSupervisors.includes('없음')}
                    onChange={() => handleSelectSupervisor('없음')}
                  />
                  없음
                </label>
              </li>
            )}
            {filteredSupervisors.map((s) => {
              const { status, isRed } = getSupervisorStatus(s.name);
              const isSelected = selectedSupervisors.includes(s.name);
              const isNoneSelected = selectedSupervisors.includes('없음'); // '없음' 선택 여부 확인
              return (
                <li key={s.id} className={isRed ? 'red-text' : ''}>
                  <label>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectSupervisor(s.name)}
                      disabled={isRed || (isNoneSelected && !isSelected)} // 중복이거나 특이사항이 있거나, '없음'이 선택되어 있으면 비활성화
                    />
                    {s.name} {status}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default MultiSelectSupervisor;
